import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Theme, themes, defaultTheme, getThemeByName } from '../styles/themes';

interface ThemeContextType {
  currentTheme: Theme;
  setTheme: (themeName: string) => void;
  availableThemes: Theme[];
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState<Theme>(defaultTheme);

  // Load saved theme from localStorage on mount
  useEffect(() => {
    const savedThemeName = localStorage.getItem('njordheim-theme');
    if (savedThemeName) {
      const theme = getThemeByName(savedThemeName);
      setCurrentTheme(theme);
      applyThemeToDocument(theme);
    } else {
      applyThemeToDocument(defaultTheme);
    }
  }, []);

  // Apply theme CSS variables to document root
  const applyThemeToDocument = (theme: Theme) => {
    const root = document.documentElement;
    const colors = theme.colors;

    // Helper to convert hex to rgb for rgba() usage
    const hexToRgb = (hex: string) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
      return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '212, 175, 55';
    };

    // Background colors
    root.style.setProperty('--color-bg-dark', colors.background.dark);
    root.style.setProperty('--color-bg-medium', colors.background.medium);
    root.style.setProperty('--color-bg-light-dark', colors.background.lightDark);
    root.style.setProperty('--color-bg-card', colors.background.card);
    root.style.setProperty('--color-bg-dark-transparent', colors.background.darkTransparent);
    root.style.setProperty('--color-bg-dark-semi-transparent', colors.background.darkSemiTransparent);
    root.style.setProperty('--color-bg-overlay', colors.background.overlay);

    // Text colors
    root.style.setProperty('--color-text-main', colors.text.main);
    root.style.setProperty('--color-text-light', colors.text.light);
    root.style.setProperty('--color-text-medium', colors.text.medium);
    root.style.setProperty('--color-text-secondary', colors.text.secondary);
    root.style.setProperty('--color-text-gold', colors.text.gold);
    root.style.setProperty('--color-text-gold2', colors.text.gold2);
    root.style.setProperty('--color-legend-glow', colors.text.legendGlow);
    root.style.setProperty('--color-text-highlight', colors.text.highlight);
    root.style.setProperty('--color-text-muted', colors.text.muted);
    root.style.setProperty('--color-text-placeholder', colors.text.placeholder);
    root.style.setProperty('--color-text-dead', colors.text.dead);

    // Link colors
    root.style.setProperty('--color-link-primary', colors.link.primary);
    root.style.setProperty('--color-link-secondary', colors.link.secondary);
    root.style.setProperty('--color-link-hover', colors.link.hover);

    // Button colors
    root.style.setProperty('--color-button-primary', colors.button.primary);
    root.style.setProperty('--color-button-primary-hover', colors.button.primaryHover);
    root.style.setProperty('--color-button-secondary', colors.button.secondary);
    root.style.setProperty('--color-button-secondary-hover', colors.button.secondaryHover);
    root.style.setProperty('--color-button-danger', colors.button.danger);
    root.style.setProperty('--color-button-danger-hover', colors.button.dangerHover);
    root.style.setProperty('--color-button-success', colors.button.success);
    root.style.setProperty('--color-button-warning', colors.button.warning);

    // Border colors
    root.style.setProperty('--color-border-primary', colors.border.primary);
    root.style.setProperty('--color-border-light', colors.border.light);

    // Status colors
    root.style.setProperty('--color-status-success', colors.status.success);
    root.style.setProperty('--color-success-rgb', hexToRgb(colors.status.success));
    root.style.setProperty('--color-status-error', colors.status.error);
    root.style.setProperty('--color-status-error-border', colors.status.errorBorder);
    root.style.setProperty('--color-status-error-border-rgb', hexToRgb(colors.status.errorBorder));
    root.style.setProperty('--color-status-warning', colors.status.warning);
    root.style.setProperty('--color-status-info', colors.status.info);
    root.style.setProperty('--color-status-critical', colors.status.critical);
    root.style.setProperty('--color-status-high', colors.status.high);
    root.style.setProperty('--color-status-medium', colors.status.medium);
    root.style.setProperty('--color-status-low', colors.status.low);

    // Progress colors
    root.style.setProperty('--color-progress-background', colors.progress.background);
    root.style.setProperty('--color-progress-fill', colors.progress.fill);
    root.style.setProperty('--color-progress-hp', colors.progress.hp);
    root.style.setProperty('--color-progress-experience', colors.progress.experience);
    root.style.setProperty('--color-progress-regen', colors.progress.regen);
    root.style.setProperty('--color-progress-bar-text', colors.progress.barText);

    // Shadow colors
    root.style.setProperty('--color-shadow-glow', colors.shadow.glow);
    root.style.setProperty('--color-shadow-dark', colors.shadow.dark);
    root.style.setProperty('--color-shadow-gold', colors.shadow.gold);
    root.style.setProperty('--color-bar-text-outline', colors.shadow.barTextOutline);

    // Spinner color
    root.style.setProperty('--color-spinner', colors.spinner);

    // Input colors
    root.style.setProperty('--color-input-background', colors.input.background);
    root.style.setProperty('--color-input-border', colors.input.border);
    root.style.setProperty('--color-input-focus-border', colors.input.focusBorder);

    // Footer colors
    root.style.setProperty('--color-footer-background', colors.footer.background);
    root.style.setProperty('--color-footer-shadow', colors.footer.shadow);

    // Header colors
    root.style.setProperty('--color-header-background', colors.header.background);

    // Auth colors
    root.style.setProperty('--color-auth-background', colors.auth.background);
    root.style.setProperty('--color-auth-input-bg', colors.auth.inputBg);

    // Combat/Team colors
    root.style.setProperty('--color-combat-player-name', colors.combat.playerName);
    root.style.setProperty('--color-combat-player-team', colors.combat.playerTeam);
    root.style.setProperty('--color-combat-enemy-team1', colors.combat.enemyTeam1);
    root.style.setProperty('--color-combat-enemy-team2', colors.combat.enemyTeam2);
    root.style.setProperty('--color-combat-enemy-team3', colors.combat.enemyTeam3);
    root.style.setProperty('--color-combat-guild-crown', colors.combat.guildCrown);

    // Table colors
    root.style.setProperty('--color-table-header-bg', colors.table.headerBg);
    root.style.setProperty('--color-table-header-text', colors.table.headerText);
    root.style.setProperty('--color-table-row-bg', colors.table.rowBg);
    root.style.setProperty('--color-table-row-alt-bg', colors.table.rowAltBg);
    root.style.setProperty('--color-table-row-hover-bg', colors.table.rowHoverBg);
    root.style.setProperty('--color-table-border', colors.table.border);
    root.style.setProperty('--color-table-text', colors.table.text);

    // Button colors (general)
    root.style.setProperty('--color-button-primary', colors.buttonGeneral.primary);
    root.style.setProperty('--color-button-primary-hover', colors.buttonGeneral.primaryHover);
    root.style.setProperty('--color-button-primary-text', colors.buttonGeneral.primaryText);
    root.style.setProperty('--color-button-secondary', colors.buttonGeneral.secondary);
    root.style.setProperty('--color-button-secondary-hover', colors.buttonGeneral.secondaryHover);
    root.style.setProperty('--color-button-secondary-text', colors.buttonGeneral.secondaryText);
    root.style.setProperty('--color-button-disabled', colors.buttonGeneral.disabled);
    root.style.setProperty('--color-button-disabled-text', colors.buttonGeneral.disabledText);

    // Rarity colors
    root.style.setProperty('--color-rarity-common', colors.rarity.common);
    root.style.setProperty('--color-rarity-uncommon', colors.rarity.uncommon);
    root.style.setProperty('--color-rarity-rare', colors.rarity.rare);
    root.style.setProperty('--color-rarity-epic', colors.rarity.epic);
    root.style.setProperty('--color-rarity-legendary', colors.rarity.legendary);
    root.style.setProperty('--color-rarity-epic-glow', `rgba(${hexToRgb(colors.rarity.epic)}, 0.5)`);

    // ===== BACKWARD COMPATIBILITY ALIASES =====
    // Set legacy variable names directly (not via var() to avoid CSS chaining issues)
    root.style.setProperty('--bg-primary', colors.background.dark);
    root.style.setProperty('--bg-secondary', colors.background.medium);
    root.style.setProperty('--bg-hover', colors.background.lightDark);
    root.style.setProperty('--background-dark', colors.background.dark);
    root.style.setProperty('--background-medium', colors.background.medium);
    root.style.setProperty('--background-light-dark', colors.background.lightDark);
    root.style.setProperty('--background-card', colors.background.card);
    
    root.style.setProperty('--text-primary', colors.text.main);
    root.style.setProperty('--text-secondary', colors.text.secondary);
    root.style.setProperty('--text-accent', colors.text.gold);
    root.style.setProperty('--text-light', colors.text.light);
    root.style.setProperty('--text-main', colors.text.main);
    root.style.setProperty('--text-gold', colors.text.gold);
    root.style.setProperty('--text-white', colors.text.light);
    root.style.setProperty('--text-accent-blue', colors.link.primary);
    root.style.setProperty('--text-error', colors.status.error);
    
    root.style.setProperty('--border-primary', colors.border.primary);
    root.style.setProperty('--border-secondary', colors.border.light);
    root.style.setProperty('--border-color', colors.border.primary);
    root.style.setProperty('--border-gold', colors.text.gold);
    root.style.setProperty('--border-accent', colors.border.primary);
    
    root.style.setProperty('--btn-primary-bg', colors.button.primary);
    root.style.setProperty('--btn-primary-hover-bg', colors.button.primaryHover);
    root.style.setProperty('--button-hover-bg', colors.button.primaryHover);

    // Status color aliases (backward compatibility)
    root.style.setProperty('--color-status-danger', colors.status.error);
    root.style.setProperty('--color-danger', colors.status.error);
    root.style.setProperty('--color-text-primary-light', colors.text.light);
    root.style.setProperty('--color-text-accent', colors.text.gold);

    // Additional legacy aliases used in older components
    root.style.setProperty('--highlight-text', colors.text.highlight);
    root.style.setProperty('--button-disabled-bg', colors.buttonGeneral.disabled);
    root.style.setProperty('--color-table-header', colors.table.headerBg);
    root.style.setProperty('--color-table-row', colors.table.rowBg);
    root.style.setProperty('--color-table-alt', colors.table.rowAltBg);
    root.style.setProperty('--color-table-hover', colors.table.rowHoverBg);
    root.style.setProperty('--card-hover-shadow', `0 0 15px rgba(${hexToRgb(colors.text.gold)}, 0.3)`);
    root.style.setProperty('--button-bg', colors.button.primary);
    root.style.setProperty('--main-text', colors.text.main);
    // Frequently-used short-name aliases
    root.style.setProperty('--color-gold', colors.text.gold);
    root.style.setProperty('--color-gold2', colors.text.gold2);
    root.style.setProperty('--color-highlight', colors.text.highlight);
    root.style.setProperty('--color-gold-rgb', hexToRgb(colors.text.gold));
    root.style.setProperty('--card-bg', colors.background.card);
    root.style.setProperty('--bg-tertiary', colors.background.lightDark);
    root.style.setProperty('--color-bg-button', colors.buttonGeneral.primary);
    root.style.setProperty('--color-bg-button-hover', colors.buttonGeneral.primaryHover);
    root.style.setProperty('--text-muted', colors.text.muted);
    root.style.setProperty('--text-medium', colors.text.medium);
    root.style.setProperty('--color-bg-secondary', colors.background.medium);
    root.style.setProperty('--color-bg-primary', colors.background.dark);
    root.style.setProperty('--color-success', colors.status.success);

    // Set theme name as data attribute for potential CSS selectors
    root.setAttribute('data-theme', theme.name);
  };

  const setTheme = async (themeName: string) => {
    const theme = getThemeByName(themeName);
    setCurrentTheme(theme);
    applyThemeToDocument(theme);
    localStorage.setItem('njordheim-theme', themeName);
    
    // Save to database if user is authenticated
    try {
      const response = await fetch('/api/user/update-theme', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({ theme: themeName }),
      });
      
      if (!response.ok) {
        console.warn('Failed to save theme preference to database');
      }
    } catch (error) {
      console.warn('Error saving theme preference:', error);
      // Don't throw - theme still works locally even if DB save fails
    }
  };

  return (
    <ThemeContext.Provider value={{ currentTheme, setTheme, availableThemes: themes }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = (): ThemeContextType => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
